import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useRole } from "../../contexts/RoleContext";
import { useCompany } from "../../contexts/CompanyContext";
import { supabase } from "../../lib/supabase";
import QRCodeDecor from "../../components/base/QRCodeDecor";



function getRepRank(rep: number) {
  if (rep >= 81) return { label: "팀장", color: "#EAB308" };
  if (rep >= 61) return { label: "과장", color: "#E8375F" };
  if (rep >= 41) return { label: "대리", color: "#FF9F43" };
  if (rep >= 21) return { label: "사원", color: "#7A9BFF" };
  return { label: "인턴", color: "#8B91A8" };
}

function generateEmployeeNumber(userId: string, createdAt: string) {
  const year = new Date(createdAt).getFullYear();
  const hash = userId.replace(/-/g, "").slice(0, 4).toUpperCase();
  return `KAP-${year}-${hash}`;
}

function Barcode({ seed }: { seed: string }) {
  // seed 기반 바 패턴 (간단한 해시)
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  h = Math.abs(h) + 1;
  const widths = Array.from({ length: 30 }, (_, i) => ((h ^ (h >> (i % 8)) ^ (i * 31)) % 3) + 1);
  return (
    <div className="flex items-end gap-[1.5px]" style={{ height: 20 }}>
      {widths.map((w, i) => (
        <div key={i} style={{
          width: w * 1.5,
          height: i % 4 === 0 ? 20 : i % 2 === 0 ? 16 : 12,
          backgroundColor: "rgba(255,255,255,0.15)",
          borderRadius: 0.5,
        }} />
      ))}
    </div>
  );
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const { jobRole, roleConfig, companyConfig } = useRole();
  const { companyState } = useCompany();

  const [displayName, setDisplayName] = useState(profile?.display_name ?? "");
  const [bio, setBio] = useState((profile as any)?.bio ?? "");
  const [phone, setPhone] = useState((profile as any)?.phone ?? "");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(profile?.avatar_url ?? null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(profile?.avatar_url ?? null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name ?? "");
      setBio((profile as any).bio ?? "");
      setPhone((profile as any).phone ?? "");
      setAvatarUrl(profile.avatar_url ?? null);
      setAvatarPreview(profile.avatar_url ?? null);
    }
  }, [profile]);

  const rank = getRepRank(companyState?.reputation ?? 30);
  const empNumber = user ? generateEmployeeNumber(user.id, profile?.created_at ?? new Date().toISOString()) : "KAP-0000-XXXX";
  const joinedDate = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" })
    : "—";

  const handlePhotoSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (file.size > 3 * 1024 * 1024) { setError("사진은 3MB 이하여야 합니다."); return; }

    setUploading(true);
    setError("");

    // 로컬 미리보기
    const reader = new FileReader();
    reader.onload = ev => setAvatarPreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    const ext = file.name.split(".").pop();
    const filePath = `${user.id}/avatar.${ext}`;

    const { error: upErr } = await supabase.storage.from("avatars").upload(filePath, file, { upsert: true });
    if (upErr) { setError("사진 업로드 실패: " + upErr.message); setUploading(false); return; }

    const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(filePath);
    const publicUrl = urlData.publicUrl + `?t=${Date.now()}`;
    setAvatarUrl(publicUrl);
    setAvatarPreview(publicUrl);
    setUploading(false);
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError("");

    const { error: err } = await supabase.from("profiles").update({
      display_name: displayName.trim(),
      bio: bio.trim(),
      phone: phone.trim(),
      avatar_url: avatarUrl,
      updated_at: new Date().toISOString(),
    }).eq("id", user.id);

    if (err) { setError("저장 실패: " + err.message); }
    else {
      await refreshProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex flex-col">
      {/* 상단 네비바 */}
      <header className="h-12 bg-white border-b border-black/[0.07] flex items-center px-5 gap-3 sticky top-0 z-20">
        <button type="button" onClick={() => navigate(-1)}
          className="w-7 h-7 flex items-center justify-center rounded-lg text-[#999999] hover:text-[#333333] hover:bg-black/[0.04] transition-colors cursor-pointer">
          <i className="ri-arrow-left-line text-sm"></i>
        </button>
        <span className="text-[#111111] text-sm font-medium">내 프로필</span>
        <span className="text-[#CCCCCC] text-[11px]">— 사원증 수정</span>
      </header>

      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* ──── 왼쪽: 사원증 카드 미리보기 ──── */}
        <div className="flex flex-col items-center justify-start gap-4">
          <p className="text-[#AAAAAA] text-xs uppercase tracking-widest">미리보기</p>

          {/* 사원증 카드 */}
          <div
            className="w-72 rounded-2xl overflow-hidden relative"
            style={{
              background: "linear-gradient(160deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)",
            }}
          >
            {/* 상단 장식 줄 */}
            <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, #4B6CF7, #7A9BFF, #4B6CF7)" }} />

            {/* 회사 헤더 */}
            <div className="px-6 pt-5 pb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#4B6CF7]/20 border border-[#4B6CF7]/30">
                  <i className={`${companyConfig.icon} text-[#7A9BFF] text-sm`}></i>
                </div>
                <div>
                  <div className="text-white text-[10px] font-bold tracking-wider">K-ArtPEN</div>
                  <div className="text-[#4B6CF7]/70 text-[8px] tracking-widest uppercase">{companyConfig.label}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-white/20 text-[8px] tracking-widest uppercase">사원증</div>
                <div className="text-white/20 text-[8px]">ID CARD</div>
              </div>
            </div>

            {/* 구분선 */}
            <div className="mx-6 h-px bg-white/[0.06]" />

            {/* 메인 콘텐츠 */}
            <div className="px-6 py-5 flex gap-4">
              {/* 증명사진 */}
              <div
                className="shrink-0 w-20 h-24 rounded-xl overflow-hidden border-2 border-white/10 cursor-pointer relative group"
                style={{ background: "linear-gradient(135deg, #1C1F2E, #252830)" }}
                onClick={() => fileInputRef.current?.click()}
              >
                {avatarPreview ? (
                  <img src={avatarPreview} alt="증명사진" className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                    <i className="ri-user-3-line text-white/20 text-2xl"></i>
                    <span className="text-white/20 text-[8px]">증명사진</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-xl">
                  <i className="ri-camera-line text-white text-lg"></i>
                </div>
                {uploading && (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-xl">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {/* 정보 */}
              <div className="flex-1 min-w-0">
                <div className="text-white text-base font-bold leading-tight mb-0.5 truncate">
                  {displayName || "이름 없음"}
                </div>
                <div className="flex items-center gap-1.5 mb-2">
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: `${rank.color}20`, color: rank.color, border: `1px solid ${rank.color}40` }}
                  >
                    {rank.label}
                  </span>
                </div>
                <div className="text-[#7A9BFF]/80 text-[10px] font-medium mb-1">{roleConfig.label.split('/')[0].trim()}</div>
                <div className="space-y-1 mt-3">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-shield-keyhole-line text-white/25 text-[9px]"></i>
                    <span className="text-white/30 text-[9px] font-mono">{empNumber}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-calendar-line text-white/25 text-[9px]"></i>
                    <span className="text-white/30 text-[9px]">입사 {joinedDate}</span>
                  </div>
                  {phone && (
                    <div className="flex items-center gap-1.5">
                      <i className="ri-phone-line text-white/25 text-[9px]"></i>
                      <span className="text-white/30 text-[9px]">{phone}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 한줄 소개 */}
            {bio && (
              <div className="mx-6 mb-3 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.05]">
                <p className="text-white/40 text-[9px] leading-relaxed line-clamp-2">&ldquo;{bio}&rdquo;</p>
              </div>
            )}

            {/* QR + 바코드 하단 영역 */}
            <div className="px-6 pb-5">
              <div className="flex items-end justify-between gap-3">
                {/* QR 코드 */}
                <div className="flex flex-col items-center gap-1">
                  <QRCodeDecor
                    seed={user?.id ?? empNumber}
                    cellSize={4}
                    opacity={0.28}
                    link={`${window.location.origin}/profile`}
                    tooltip="내 프로필 링크 — 클릭하여 열기"
                  />
                  <span className="text-white/15 text-[7px] font-mono">SCAN</span>
                </div>
                {/* 바코드 */}
                <div className="flex flex-col items-center gap-1 flex-1">
                  <Barcode seed={empNumber} />
                  <span className="text-white/15 text-[7px] font-mono tracking-widest">{empNumber}</span>
                </div>
              </div>
            </div>

            {/* 하단 장식 줄 */}
            <div className="h-0.5 w-full" style={{ background: "linear-gradient(90deg, transparent, #4B6CF7, transparent)" }} />
          </div>

          <p className="text-[#CCCCCC] text-[11px] text-center leading-relaxed">
            카드의 증명사진 부분을 클릭하거나<br />아래 폼에서 사진을 업로드하세요
          </p>
        </div>

        {/* ──── 오른쪽: 편집 폼 ──── */}
        <div className="space-y-6">
          <div>
            <h2 className="text-[#111111] text-lg font-semibold mb-0.5">프로필 수정</h2>
            <p className="text-[#AAAAAA] text-xs">변경 사항은 저장 버튼을 눌러야 반영됩니다</p>
          </div>

          {/* 증명사진 업로드 */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-2 uppercase tracking-wider">증명사진</label>
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-20 rounded-xl overflow-hidden border-2 border-dashed border-[#DDDDDD] hover:border-[#4B6CF7]/40 cursor-pointer transition-colors flex items-center justify-center bg-[#F7F8FA] relative group"
                onClick={() => fileInputRef.current?.click()}
              >
                {avatarPreview ? (
                  <img src={avatarPreview} alt="프로필" className="w-full h-full object-cover object-top" />
                ) : (
                  <i className="ri-user-3-line text-[#CCCCCC] text-xl"></i>
                )}
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-xl">
                  <i className="ri-camera-line text-white text-sm"></i>
                </div>
              </div>
              <div className="flex-1">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full py-2 rounded-lg border border-[#DDDDDD] hover:border-[#4B6CF7]/40 text-[#555555] hover:text-[#4B6CF7] text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40"
                >
                  {uploading ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <div className="w-3.5 h-3.5 border border-[#CCCCCC] border-t-[#555555] rounded-full animate-spin"></div>
                      업로드 중...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-1.5">
                      <i className="ri-upload-2-line"></i>
                      사진 선택
                    </span>
                  )}
                </button>
                <p className="text-[#CCCCCC] text-[10px] mt-1.5">JPG, PNG · 최대 3MB · 정면 사진 권장</p>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handlePhotoSelect}
              className="hidden"
            />
          </div>

          {/* 구분선 */}
          <div className="h-px bg-[#EEEEEE]" />

          {/* 이름 */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-2 uppercase tracking-wider">이름</label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="실명 또는 닉네임"
              maxLength={20}
              className="w-full border border-[#E0E0E0] focus:border-[#4B6CF7]/50 outline-none rounded-lg px-4 py-2.5 text-[#111111] text-sm placeholder-[#CCCCCC] transition-colors bg-white"
            />
          </div>

          {/* 연락처 */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-2 uppercase tracking-wider">연락처</label>
            <input
              type="text"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="010-0000-0000"
              maxLength={20}
              className="w-full border border-[#E0E0E0] focus:border-[#4B6CF7]/50 outline-none rounded-lg px-4 py-2.5 text-[#111111] text-sm placeholder-[#CCCCCC] transition-colors bg-white"
            />
          </div>

          {/* 한줄 소개 */}
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-2 uppercase tracking-wider">
              한줄 소개 <span className="text-[#CCCCCC] normal-case font-normal">(사원증에 표시)</span>
            </label>
            <textarea
              value={bio}
              onChange={e => setBio(e.target.value.slice(0, 80))}
              placeholder="예: 음악과 사람을 연결하는 A&R 담당자입니다"
              rows={2}
              maxLength={80}
              className="w-full border border-[#E0E0E0] focus:border-[#4B6CF7]/50 outline-none rounded-lg px-4 py-2.5 text-[#111111] text-sm placeholder-[#CCCCCC] transition-colors bg-white resize-none"
            />
            <div className="text-right text-[#CCCCCC] text-[10px] mt-1">{bio.length}/80</div>
          </div>

          {/* 읽기 전용 정보 */}
          <div className="bg-[#F0F2F8] border border-[#E5E8F0] rounded-xl px-5 py-4 space-y-3">
            <p className="text-[#8B91A8] text-[10px] uppercase tracking-wider font-semibold mb-1">자동 입력 정보</p>
            {[
              { icon: "ri-shield-keyhole-line", label: "사번", value: empNumber },
              { icon: "ri-briefcase-line", label: "직무", value: roleConfig.label.split('/')[0].trim() },
              { icon: "ri-award-line", label: "직급", value: rank.label },
              { icon: "ri-mail-line", label: "이메일", value: profile?.email ?? "—" },
              { icon: "ri-calendar-line", label: "입사일", value: joinedDate },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-[#8B91A8] text-sm`}></i>
                </div>
                <span className="text-[#8B91A8] text-xs w-14 shrink-0">{item.label}</span>
                <span className="text-[#555555] text-xs font-medium">{item.value}</span>
              </div>
            ))}
          </div>

          {/* 에러 */}
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
              <i className="ri-error-warning-line text-red-400 text-sm"></i>
              <p className="text-red-500 text-xs">{error}</p>
            </div>
          )}

          {/* 저장 버튼 */}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !displayName.trim()}
            className="w-full py-3 rounded-xl font-semibold text-sm transition-all whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: saved ? "#10B981" : "linear-gradient(135deg, #4B6CF7, #7A9BFF)", color: "white" }}
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                저장 중...
              </span>
            ) : saved ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-checkbox-circle-fill"></i>
                저장 완료!
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-save-line"></i>
                프로필 저장
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
